import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormFieldComponent } from '../form-field/form-field.component';
import { FormFieldConfig } from '../form-field/form-field.types';
import {
	FormFieldValue,
	MajFormCanvasContext,
	MajFormCanvasField,
	MajFormCanvasLabel,
	MajFormCanvasSelection,
	MajFormCanvasSelectionStyles
} from './dynamic-test.types';

@Component({
	selector: 'maj-dynamic-test',
	standalone: true,
	imports: [CommonModule, FormFieldComponent],
	templateUrl: './dynamic-test.component.html',
	styleUrls: ['./dynamic-test.component.scss'],
})
export class DynamicTestComponent {
	@Input()
	title = '';

	@Input()
	width = 0;

	@Input()
	height = 0;

	@Input()
	labels: MajFormCanvasLabel[] = [];

	@Input()
	fields: MajFormCanvasField[] = [];

	@Input()
	values?: Record<string, FormFieldValue>;

	@Input()
	selected?: MajFormCanvasSelection;

	@Input()
	selectionStyles?: MajFormCanvasSelectionStyles;

	@Input()
	readOnly = false;

	@Output()
	headerClick = new EventEmitter<MajFormCanvasContext | void>();

	@Output()
	canvasClick = new EventEmitter<{ x: number, y: number } & MajFormCanvasContext>();

	@Output()
	labelClick = new EventEmitter<{ label: MajFormCanvasLabel } & MajFormCanvasContext>();

	@Output()
	fieldSelected = new EventEmitter<{ field: MajFormCanvasField } & MajFormCanvasContext>();

	@Output()
	fieldValueChange = new EventEmitter<{ id: string, value: FormFieldValue }>();

	get headerBackground(): string | null {
		const isSelected = !!this.selected?.header;
		const color = this.selectionStyles?.headerSelectedBackground ?? '#e9e9e9';
		return isSelected ? color : null;
	}

	onFieldValueChange(value: FormFieldValue, field: MajFormCanvasField) {
		this.fieldValueChange.emit({id: field.id, value});
	}

	onHeaderClick($event: MouseEvent) {
		this.headerClick.emit({originalEvent: $event});
	}

	onCanvasClick($event: MouseEvent) {
		if (this.readOnly) return;
		const rectangle = ($event.currentTarget as HTMLElement).getBoundingClientRect();
		this.canvasClick.emit({x: $event.clientX - rectangle.left, y: $event.clientY - rectangle.top, originalEvent: $event});
	}

	onLabelClick($event: MouseEvent, label: MajFormCanvasLabel) {
		$event.stopPropagation();
		this.labelClick.emit({label, originalEvent: $event});
	}

	onFieldSelected(ctx: { $event: MouseEvent, id: string | number, value: unknown }, field: MajFormCanvasField) {
		this.fieldSelected.emit({field, originalEvent: ctx.$event});
	}

	isLabelSelected(label: MajFormCanvasLabel): boolean {
		const ids = this.normalizeIdSet(this.selected?.labelIds);
		return ids.has(label.id);
	}

	isFieldSelected(field: MajFormCanvasField): boolean {
		const ids = this.normalizeIdSet(this.selected?.fieldIds);
		return ids.has(field.id);
	}

	private normalizeIdSet(ids?: Set<string> | string[]): Set<string> {
		if (!ids) return new Set<string>();
		return ids instanceof Set ? ids : new Set(ids);
	}

	mergedConfig(field: MajFormCanvasField): FormFieldConfig {
		const base: FormFieldConfig = {...field.config} as FormFieldConfig;
		if (field.position) {
			base.top = field.position.top;
			base.left = field.position.left;
			base.width = field.position.width;
			base.height = field.position.height;
		}
		return base;
	}

	labelStyles(label: MajFormCanvasLabel): { [key: string]: string } {
		const selected = this.isLabelSelected(label);
		const background = selected ? (this.selectionStyles?.labelSelectedBackground ?? 'lightblue') : 'white';
		const styles: any = {
			'position': 'absolute',
			'top': `${label.top}px`,
			'left': `${label.left}px`,
			'text-align': label.textAlign ?? 'left',
			'background-color': background,
		};

		if (label.width != null) {
			styles['width'] = `${label.width}px`;
		}
		if (label.height != null) {
			styles['height'] = `${label.height}px`;
		}

		return styles;
	}

	trackByLabelId = (_id: number, label: MajFormCanvasLabel) => label.id;
	trackByFieldId = (_id: number, field: MajFormCanvasField) => field.id;

}
